import {
  artifactDraftResponseSchema,
  artifactDraftBatchResponseSchema,
  chatActionsEventSchema,
  chatSourceSchema,
  chatUsageSchema,
  contextMetricsSchema,
  parseExternal,
  workerErrorResponseSchema,
  type ArtifactDraft,
  type ContextualAction,
  type ChatRequest,
  type ChatSource,
  type ChatUsage,
  type ContextMetrics,
  type GenerateArtifactRequest,
  type GenerateArtifactBatchRequest,
} from "@base-hormonal/contracts";
import { z } from "zod";
import { PublicAppError } from "../../../shared/lib/errors";
import { workerEndpoint } from "../../../shared/api/worker";

/** SSE pode durar bem mais que uma leitura comum; o cancelamento é do usuário. */
export const CHAT_TIMEOUT_MS = 310_000;
// Maior que o teto de 180 s do Worker para receber seu erro tipado, se houver.
export const ARTIFACT_TIMEOUT_MS = 240_000;

export interface ChatStreamHandlers {
  onSources?(sources: ChatSource[]): void;
  onContext?(metrics: ContextMetrics): void;
  onDelta?(text: string): void;
  onUsage?(usage: ChatUsage): void;
  onActions?(actions: ContextualAction[]): void;
}

export interface ChatStreamResult {
  answer: string;
  sources: ChatSource[];
  usage: ChatUsage | null;
  contextMetrics: ContextMetrics | null;
  requestId: string | null;
  chunks: number;
  actions: ContextualAction[];
}

const deltaSchema = z.object({ text: z.string() }).strict();

function publicError(body: unknown, fallback: string): PublicAppError {
  const parsed = workerErrorResponseSchema.safeParse(body);
  if (!parsed.success) return new PublicAppError("request_failed", fallback);
  const details = parsed.data.validationErrors ?? [];
  const message =
    details.length > 0 ? `${parsed.data.message} — ${details.join(" — ")}` : parsed.data.message;
  return new PublicAppError(parsed.data.code, message);
}

/**
 * Combina o cancelamento do usuário com um teto de tempo, sem descartar a
 * razão original: cancelado e expirado geram códigos distintos.
 */
function scopedSignal(
  parent: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; dispose(): void; timedOut(): boolean } {
  const controller = new AbortController();
  let expired = false;
  const forward = (): void => controller.abort(parent?.reason);
  if (parent?.aborted === true) controller.abort(parent.reason);
  else parent?.addEventListener("abort", forward, { once: true });
  const timer = window.setTimeout(() => {
    expired = true;
    controller.abort("timeout");
  }, timeoutMs);
  return {
    signal: controller.signal,
    dispose: () => {
      window.clearTimeout(timer);
      parent?.removeEventListener("abort", forward);
    },
    timedOut: () => expired,
  };
}

function abortFailure(parent: AbortSignal | undefined, timedOut: boolean): PublicAppError {
  if (parent?.aborted === true && !timedOut) {
    return new PublicAppError("request_cancelled", "A resposta foi cancelada.");
  }
  return new PublicAppError(
    "request_timeout",
    "A resposta demorou mais que o esperado. Tente novamente.",
  );
}

/** Consome o SSE do Worker validando cada evento antes de tocar a interface. */
export async function streamChat(
  accessToken: string,
  body: ChatRequest,
  handlers: ChatStreamHandlers = {},
  parentSignal?: AbortSignal,
): Promise<ChatStreamResult> {
  const scoped = scopedSignal(parentSignal, CHAT_TIMEOUT_MS);
  const result: ChatStreamResult = {
    answer: "",
    sources: [],
    usage: null,
    contextMetrics: null,
    requestId: null,
    chunks: 0,
    actions: [],
  };
  try {
    const response = await fetch(workerEndpoint("/api/chat"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
      signal: scoped.signal,
    });
    result.requestId = response.headers.get("X-Request-Id");
    if (!response.ok || response.body === null) {
      const failure: unknown = await response.json().catch(() => null);
      throw publicError(failure, "Não foi possível iniciar a resposta.");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // O cancelamento precisa encerrar a leitura mesmo quando o transporte não
    // rejeita o `read()` pendente: o laço confere o sinal a cada iteração.
    const stopReading = (): void => void reader.cancel().catch(() => undefined);
    scoped.signal.addEventListener("abort", stopReading, { once: true });
    for (;;) {
      if (scoped.signal.aborted) throw abortFailure(parentSignal, scoped.timedOut());
      const { done, value } = await reader.read();
      if (scoped.signal.aborted) throw abortFailure(parentSignal, scoped.timedOut());
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        applyEvent(raw, result, handlers);
        boundary = buffer.indexOf("\n\n");
      }
    }
    return result;
  } catch (error: unknown) {
    if (error instanceof PublicAppError) throw error;
    if (scoped.signal.aborted) throw abortFailure(parentSignal, scoped.timedOut());
    throw new PublicAppError("request_failed", "Não foi possível concluir a resposta.");
  } finally {
    scoped.dispose();
  }
}

function applyEvent(raw: string, result: ChatStreamResult, handlers: ChatStreamHandlers): void {
  const name = /^event: (.+)$/m.exec(raw)?.[1];
  const payload = /^data: (.+)$/m.exec(raw)?.[1];
  if (name === undefined || payload === undefined) return;
  const external: unknown = JSON.parse(payload);
  if (name === "sources") {
    result.sources = parseExternal(z.array(chatSourceSchema).max(50), external);
    handlers.onSources?.(result.sources);
    return;
  }
  if (name === "context") {
    result.contextMetrics = parseExternal(contextMetricsSchema, external);
    handlers.onContext?.(result.contextMetrics);
    return;
  }
  if (name === "delta") {
    const delta = parseExternal(deltaSchema, external);
    result.answer += delta.text;
    result.chunks += 1;
    handlers.onDelta?.(delta.text);
    return;
  }
  if (name === "usage") {
    result.usage = parseExternal(chatUsageSchema, external);
    handlers.onUsage?.(result.usage);
    return;
  }
  if (name === "actions") {
    const event = parseExternal(chatActionsEventSchema, external);
    result.answer = event.answer;
    result.actions = event.actions;
    handlers.onActions?.(event.actions);
    return;
  }
  if (name === "error") {
    throw publicError(external, "Não foi possível concluir a resposta.");
  }
}

async function postJson(
  path: string,
  accessToken: string,
  body: unknown,
  parentSignal: AbortSignal | undefined,
): Promise<unknown> {
  const scoped = scopedSignal(parentSignal, ARTIFACT_TIMEOUT_MS);
  try {
    const response = await fetch(workerEndpoint(path), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
      signal: scoped.signal,
    });
    const external: unknown = await response.json().catch(() => null);
    if (!response.ok) throw publicError(external, "Não foi possível concluir a operação.");
    return external;
  } catch (error: unknown) {
    if (error instanceof PublicAppError) throw error;
    if (scoped.signal.aborted) throw abortFailure(parentSignal, scoped.timedOut());
    throw new PublicAppError("request_failed", "Não foi possível concluir a operação.");
  } finally {
    scoped.dispose();
  }
}

export async function generateDraft(
  accessToken: string,
  body: GenerateArtifactRequest,
  signal?: AbortSignal,
): Promise<ArtifactDraft> {
  const external = await postJson("/api/ai-drafts/generate", accessToken, body, signal);
  return parseExternal(artifactDraftResponseSchema, external).draft;
}

export async function generateDraftBatch(
  accessToken: string,
  body: GenerateArtifactBatchRequest,
  signal?: AbortSignal,
): Promise<ArtifactDraft[]> {
  const external = await postJson("/api/ai-drafts/generate-batch", accessToken, body, signal);
  return parseExternal(artifactDraftBatchResponseSchema, external).drafts;
}

export async function reviseDraft(
  accessToken: string,
  draftId: string,
  payload: unknown,
  signal?: AbortSignal,
): Promise<ArtifactDraft> {
  const external = await postJson(
    `/api/ai-drafts/${encodeURIComponent(draftId)}/revise`,
    accessToken,
    { payload },
    signal,
  );
  return parseExternal(artifactDraftResponseSchema, external).draft;
}
